import { DashboardAndAnalysisFiltersPreview } from '@modules/analysis/components/DashboardAndAnalysisFiltersPreview';
import { AndOperatorLabel } from '@modules/analysis/components/DashboardAndAnalysisFiltersPreview/AndOperatorLabel';
import { WhereClauseField } from '@modules/common/components/WhereClauseField';
import { WhereClausePreviewProps } from '@modules/common/components/WhereClausePreview/WhereClausePreview';
import { isEmptyWhereClause } from '@modules/common/utils';
import {
  CombinedDashboardFilterField,
  CombinedQuantifyFilterField,
} from '@modules/quantify/components/QuantifyQueryBuilder/Fields/CombinedDashboadQuantifyFilterFields';
import {
  DEFAULT_COMPARE_FILTER_A_NAME,
  DEFAULT_COMPARE_FILTER_B_NAME,
} from '@modules/quantify/constants';
import {
  useFetchQuantifyReport,
  useGetQuantifyCompareQuery,
  useGetQuantifyDenominatorWhereClause,
} from '@modules/quantify/hooks';
import { useGetCompareColors } from '@modules/quantify/hooks/useGetCompareColors';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { isValidQuery } from '@modules/quantify/utils';
import { QueryBuilderStoreProvider } from '@modules/queryBuilder/store/QueryBuilderStoreProvider';
import { Box, Stack, Typography } from '@mui/material';
import { AnalysisUseCase } from 'generated/graphql';
import { useCallback, useEffect, useState } from 'react';
import {
  EmptyWhereClause,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';

interface MeasuredAsMetricDenominatorPreviewProps {
  isFilterA: boolean;
  isReadOnly?: boolean;
  whereClausePreviewProps?: Partial<WhereClausePreviewProps>;
  shouldIncludeDashboardFilters: boolean;
  shouldShowDashboardFiltersSeparately: boolean;
}

export const MeasuredAsMetricCompareDenominatorPreview = ({
  isFilterA,
  isReadOnly,
  whereClausePreviewProps,
  shouldIncludeDashboardFilters,
  shouldShowDashboardFiltersSeparately,
}: MeasuredAsMetricDenominatorPreviewProps) => {
  const { colorA, colorB } = useGetCompareColors();

  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const compareQuery = useGetQuantifyCompareQuery({
    shouldIncludeDashboardFilters:
      shouldIncludeDashboardFilters && !shouldShowDashboardFiltersSeparately,
  });
  const denominatorWhereClause = useGetQuantifyDenominatorWhereClause({
    shouldIncludeDashboardFilters:
      shouldIncludeDashboardFilters && !shouldShowDashboardFiltersSeparately,
  });
  const dashboardWhereClause = useQuantifyStore(
    ({ visual }) => visual.dashboardFilters?.whereClause,
  );
  const isValidQueryState = useQuantifyStore(({ query }) =>
    isValidQuery(query),
  );

  const compareFilterAName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.baseQueryName || DEFAULT_COMPARE_FILTER_A_NAME,
  );
  const compareFilterBName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.deltaQueryName || DEFAULT_COMPARE_FILTER_B_NAME,
  );

  const [shouldOpenFilterModal, setShouldOpenFilterModal] = useState(false);

  const fetchReportData = useFetchQuantifyReport();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);

  useEffect(() => {
    if (shouldFetchReportData && isValidQueryState) {
      setShouldFetchReportData(false);
      fetchReportData();
    }
  }, [fetchReportData, isValidQueryState, shouldFetchReportData]);

  const onChange = useCallback(
    ({ whereClause }: { whereClause: WhereClause | null }) => {
      if (!compareQuery?.whereClause) {
        return;
      }

      if (isFilterA) {
        dispatch({
          type: QuantifyActionType.SET_QUERY,
          payload: {
            formulaQuery: {
              denominatorWhereClause: whereClause ?? undefined,
            },
          },
        });
      } else {
        dispatch({
          type: QuantifyActionType.SET_QUERY,
          payload: {
            compareQuery: {
              whereClause: compareQuery.whereClause,
              formula: {
                denominatorWhereClause: whereClause ?? undefined,
              },
            },
          },
        });
      }
      setShouldFetchReportData(true);
    },
    [dispatch, isFilterA, compareQuery],
  );

  const renderWhereClauseField = useCallback(() => {
    if (!compareQuery?.whereClause) {
      return null;
    }

    if (
      !dashboardWhereClause ||
      !shouldIncludeDashboardFilters ||
      !shouldShowDashboardFiltersSeparately
    ) {
      return (
        <WhereClauseField
          whereClause={
            isFilterA
              ? denominatorWhereClause
              : compareQuery.formula?.denominatorWhereClause
          }
          onChange={onChange}
          modalTitle={`Add Denominator for ${
            isFilterA ? compareFilterAName : compareFilterBName
          }`}
          label={null}
          shouldOpenModal={shouldOpenFilterModal}
          onShouldOpenModelChange={(shouldOpen) => {
            setShouldOpenFilterModal(shouldOpen);
          }}
          disableOpenModal={isReadOnly}
          whereClausePreviewProps={whereClausePreviewProps}
        />
      );
    }

    return (
      <Stack>
        <CombinedDashboardFilterField
          isReadOnly={isReadOnly}
          onRemove={() => setShouldFetchReportData(true)}
        />
        <AndOperatorLabel />
        <CombinedQuantifyFilterField
          whereClause={
            isFilterA
              ? denominatorWhereClause
              : compareQuery.formula?.denominatorWhereClause
          }
          onChange={onChange}
          modalTitle={`Add Denominator for ${
            isFilterA ? compareFilterAName : compareFilterBName
          }`}
          customPreviewTitle={`Denominator for ${
            isFilterA ? compareFilterAName : compareFilterBName
          }`}
          label={null}
          shouldOpenModal={shouldOpenFilterModal}
          onShouldOpenModelChange={(shouldOpen) => {
            setShouldOpenFilterModal(shouldOpen);
          }}
          disableOpenModal={isReadOnly}
          whereClausePreviewProps={whereClausePreviewProps}
          onClear={() => {
            onChange({ whereClause: null });
          }}
        />
      </Stack>
    );
  }, [
    compareQuery?.whereClause,
    compareQuery?.formula?.denominatorWhereClause,
    dashboardWhereClause,
    shouldIncludeDashboardFilters,
    shouldShowDashboardFiltersSeparately,
    isReadOnly,
    isFilterA,
    denominatorWhereClause,
    onChange,
    compareFilterAName,
    compareFilterBName,
    shouldOpenFilterModal,
    whereClausePreviewProps,
  ]);

  if (!compareQuery?.whereClause) {
    return null;
  }

  const renderCompareFilterName = () => {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Box
          sx={{
            backgroundColor: isFilterA ? colorA : colorB,
            borderRadius: '50%',
            p: 0.5,
            width: '4px',
          }}
        />
        <Typography variant="caption" color="inherit">
          {isFilterA ? compareFilterAName : compareFilterBName}
        </Typography>
      </Stack>
    );
  };

  const isWhereClauseAbsent = isFilterA
    ? isEmptyWhereClause(denominatorWhereClause ?? EmptyWhereClause)
    : isEmptyWhereClause(
        compareQuery?.formula?.denominatorWhereClause ?? EmptyWhereClause,
      );

  if (isReadOnly && isWhereClauseAbsent) {
    if (dashboardWhereClause) {
      return (
        <Stack direction="column" spacing={0.5}>
          {renderCompareFilterName()}
          <DashboardAndAnalysisFiltersPreview
            dashboardWhereClause={dashboardWhereClause}
            analysisWhereClause={
              isFilterA
                ? denominatorWhereClause ?? EmptyWhereClause
                : compareQuery.formula?.denominatorWhereClause ??
                  EmptyWhereClause
            }
            analysisUseCase={AnalysisUseCase.Default}
            shouldHideHeader
          />
        </Stack>
      );
    }

    return (
      <Stack direction="column" spacing={0.5}>
        {renderCompareFilterName()}
        <Typography variant="body2" color="inherit">
          No filters applied
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="column" spacing={0.5}>
      {renderCompareFilterName()}
      <QueryBuilderStoreProvider>
        {renderWhereClauseField()}
      </QueryBuilderStoreProvider>
    </Stack>
  );
};
